import React from 'react';
import { Card, CardContent, type CardProps, alpha } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Enhanced card with hover animations and professional styling
const StyledCard = styled(Card)<{
  cardVariant?: 'elevated' | 'outlined' | 'gradient';
}>(({ theme, cardVariant = 'elevated' }) => ({
  background:
    cardVariant === 'gradient'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
      : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-out',
  cursor: 'default',
  overflow: 'visible',
  position: 'relative',

  ...(cardVariant === 'outlined' && {
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  }),

  '&:hover': {
    boxShadow:
      cardVariant === 'elevated'
        ? `0 12px 24px ${alpha(theme.palette.primary.main, 0.3)}`
        : `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: theme.palette.primary.main,
  },

  '&:active': {
    opacity: 0.9,
  },
}));

export interface EnhancedCardProps extends CardProps {
  cardVariant?: 'elevated' | 'outlined' | 'gradient';
  hoverEffect?: boolean;
}

export const EnhancedCard: React.FC<EnhancedCardProps> = ({
  cardVariant = 'elevated',
  hoverEffect = true,
  children,
  sx = {},
  ...props
}) => {
  return (
    <StyledCard
      cardVariant={cardVariant}
      sx={{
        ...(!hoverEffect && {
          '&:hover': {
            boxShadow: 'inherit',
            borderColor: 'inherit',
          },
          '&:active': {
            opacity: 1,
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

// Stats card component similar to AlertsPage stats
export interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  loading?: boolean;
}

export const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon, color, loading = false }) => {
  const theme = useTheme();
  const cardColor = color || theme.palette.primary.main;

  return (
    <EnhancedCard
      cardVariant='gradient'
      sx={{
        background: `linear-gradient(135deg, ${alpha(cardColor, 0.1)} 0%, ${alpha(cardColor, 0.05)} 100%)`,
        border: `1px solid ${alpha(cardColor, 0.2)}`,
        textAlign: 'center',
        minHeight: 120,
      }}
    >
      <CardContent
        sx={{
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <div style={{ color: cardColor, fontSize: 32 }}>{icon}</div>
        <div>
          {loading ? (
            <div
              style={{
                width: 60,
                height: 32,
                background: alpha(cardColor, 0.1),
                borderRadius: 4,
              }}
            />
          ) : (
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: cardColor,
                lineHeight: 1,
              }}
            >
              {value}
            </div>
          )}
          <div
            style={{
              fontSize: '0.75rem',
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              marginTop: 4,
            }}
          >
            {title}
          </div>
        </div>
      </CardContent>
    </EnhancedCard>
  );
};
